/**
 * Every move's own sound (lane C0-SOUND; docs/research/C0_SOUND.md): `skill.move.<skill id>` for each of the shared
 * skills, composed from its art's family and its plan (sfx/moves/). Long moves add one sound per beat
 * (`skill.beat.<id>`: each warp of a chain, each turn of a whirlwind); each family adds a blow on the target
 * (`skill.impact.<family>`) and a wind-up colour (`skill.windup.<family>`). sfx/events.ts plays them.
 *
 * Levels are evened out at load: each move's loudest 120 ms (read from its score, sfx/score.ts) is brought to its
 * kind's target, so a light poke never drowns a power move and no art is louder than another by accident.
 */
import { SKILLS, type SkillDef } from '@nine/shared';
import { composeBeat, composeImpact, composeMove, composeWindup } from '../moves/compose';
import { FAMILIES, type Family } from '../moves/families';
import { familyIdOf, planOf, type MovePlan } from '../moves/plan';
import { registerSfx, type SfxCall } from '../registry';
import { quickLoudness, ScoreKit } from '../score';
import type { SfxKit } from '../kit';
// The effects' sounds (`skill.fx.*`) are layers of the monsters' moves: registered first.
import './skills';

/** Target loudness (energy of the loudest 120 ms) by kind, relative to an attack. */
const TARGET: Readonly<Record<string, number>> = { attack: 1, break: 1.25, power: 1.6, heal: 0.45, buff: 0.45 };
/** An attack's loudest 120 ms, in score units (the element sounds sit about here). */
const BASE = 0.03;

/** Register of a move (`MovePlan.lift`): as written, a tritone lower, a tritone higher. */
const LIFT = [1, 0.707, 1.414] as const;

/** The level that brings a sound function to `target`. */
function levelFor(play: (k: SfxKit, o: SfxCall) => void, target: number, pitch = 1): number {
  const o: SfxCall = { intensity: 1, len: undefined };
  const k = new ScoreKit(7, o, pitch);
  play(k, o);
  const loud = quickLoudness(k.voices);
  if (loud <= 0) return 1;
  return Math.max(0.35, Math.min(2.2, Math.sqrt((BASE * target) / loud)));
}

const familyOf = (s: SkillDef): Family => FAMILIES[familyIdOf(s)] ?? FAMILIES.outlaw!;

/** How long the review renders a move: its last blow plus its tail. */
function previewOf(p: MovePlan, s: SkillDef): number {
  const last = p.strikes.length ? p.strikes[p.strikes.length - 1]! : 0;
  if (p.shape === 'heal') return 1.8;
  if (s.id === 'ss-hatred') return 3.4;
  return Math.min(3.2, Math.max(1, last + 1.2 + (s.kind === 'power' ? 0.6 : 0)));
}

export const MOVE_PLANS = new Map<string, MovePlan>();

for (const s of Object.values(SKILLS)) {
  const p = planOf(s);
  MOVE_PLANS.set(s.id, p);
  const f = familyOf(s);
  const play = (k: SfxKit) => composeMove(k, s, p, f);
  registerSfx(
    `skill.move.${s.id}`,
    {
      label: `${s.name} (${f.label})`,
      cat: 'skill',
      vary: 0.025,
      gap: 0.05,
      pitch: LIFT[p.lift] * p.size,
      gain: levelFor(play, TARGET[s.kind] ?? 1, LIFT[p.lift] * p.size),
      preview: previewOf(p, s),
    },
    play,
  );
  if (p.shape === 'blink' || p.shape === 'spin') {
    const beat = (k: SfxKit) => composeBeat(k, s, p, f);
    registerSfx(
      `skill.beat.${s.id}`,
      {
        label: `${s.name} · จังหวะ`,
        cat: 'skill',
        vary: 0.03,
        gap: 0.04,
        pitch: LIFT[p.lift] * p.size,
        gain: levelFor(beat, 0.8, LIFT[p.lift] * p.size),
        preview: 1.4,
      },
      beat,
    );
  }
}

for (const [id, f] of Object.entries(FAMILIES)) {
  const impact = (k: SfxKit) => composeImpact(k, f);
  registerSfx(
    `skill.impact.${id}`,
    { label: `กระทบ · ${f.label}`, cat: 'combat', vary: 0.04, gap: 0.03, gain: levelFor(impact, 0.45) },
    impact,
  );
  const windup = (k: SfxKit, o: SfxCall) => composeWindup(k, f, o.len ?? 0.5);
  registerSfx(
    `skill.windup.${id}`,
    { label: `รวมปราณ · ${f.label}`, cat: 'skill', vary: 0.02, gap: 0.1, gain: levelFor(windup, 0.3), preview: 1 },
    windup,
  );
}
